import React from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import CheckBoxOutlineBlankOutlined from "@mui/icons-material/CheckBoxOutlineBlankOutlined";
import CheckBoxOutlined from "@mui/icons-material/CheckBoxOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import FolderOutlined from "@mui/icons-material/FolderOutlined";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import { Link as RouterLink } from "react-router-dom";
import { ICandidateListItem, salaryFormatter } from "../../data/Candidates";
import { ICandidateFilter } from "./CandidateListPage";

interface CandidateRowProps {
  candidate: ICandidateListItem;
  filter: ICandidateFilter;
}

const dateOptions: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "numeric",
  day: "numeric",
};

function CandidateListTableRow({ candidate, filter }: CandidateRowProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <TableRow
        tabIndex={-1}
        className={
          candidate.status === "Placed" ? "candidate-placed" : undefined
        }
        sx={{
          ...{ "& > *": { borderBottom: "unset !important" } },
          ...(candidate.status === "Placed"
            ? { outline: "1.5px solid red", outlineOffset: "-1.5px" }
            : {}),
        }}
      >
        <TableCell scope="row" align="left">
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell scope="row" align="left">
          {new Date(candidate.modified).toLocaleDateString(
            "en-GB",
            dateOptions
          )}
        </TableCell>
        <TableCell scope="row" align="left">
          {candidate.name}
        </TableCell>
        <TableCell scope="row" align="left">
          {candidate.nationality}
        </TableCell>
        <TableCell scope="row" align="left">
          {salaryFormatter.format(candidate.salary)}
        </TableCell>
        <TableCell align="right" padding="none">
          <IconButton
            href={candidate.folder ?? ""}
            target="_blank"
            rel="noreferrer noopener"
          >
            <FolderOutlined />
          </IconButton>
          <IconButton
            component={RouterLink}
            to={`/candidates/${candidate.id}/`}
          >
            <EditOutlined />
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          sx={{ pb: 0, pt: 0, backgroundColor: "grey.100" }}
          colSpan={6}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ display: "flex", margin: 1 }}>
              <Box sx={{ flex: "2 1 0" }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell width="40%">Email</TableCell>
                      <TableCell align="left">{candidate.email}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell width="45%">Status</TableCell>
                      <TableCell align="left">{candidate.status}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell width="45%">Driving Licence</TableCell>
                      <TableCell align="left">
                        {candidate.driving_licence}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell width="45%">Created</TableCell>
                      <TableCell align="left">
                        {new Date(candidate.created).toLocaleDateString(
                          "en-GB",
                          dateOptions
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell width="45%">Valid Work Permit</TableCell>
                      <TableCell>
                        {candidate.valid_work_permit ? (
                          <CheckBoxOutlined fontSize="small" />
                        ) : (
                          <CheckBoxOutlineBlankOutlined fontSize="small" />
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell width="45%">BEE Qualified</TableCell>
                      <TableCell>
                        {candidate.qualifies_for_bee ? (
                          <CheckBoxOutlined fontSize="small" />
                        ) : (
                          <CheckBoxOutlineBlankOutlined fontSize="small" />
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell width="45%">Permanent Resident</TableCell>
                      <TableCell>
                        {candidate.permanent_resident ? (
                          <CheckBoxOutlined fontSize="small" />
                        ) : (
                          <CheckBoxOutlineBlankOutlined fontSize="small" />
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
              <Box sx={{ flex: "4 1 0", ml: 2 }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell width="120px">Experience</TableCell>
                      <TableCell align="left">
                        {candidate.experience.map((experience) => (
                          <Chip
                            key={experience.id}
                            color={
                              filter.experience.includes(experience.id)
                                ? "info"
                                : "default"
                            }
                            size="small"
                            label={experience.name}
                            sx={{ m: "1px" }}
                          />
                        ))}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell width="120px">Skills</TableCell>
                      <TableCell align="left">
                        {candidate.technical_skills.map((skill) => (
                          <Chip
                            key={skill.id}
                            color={
                              filter.technical_skills.includes(skill.id)
                                ? "info"
                                : "default"
                            }
                            size="small"
                            label={skill.name}
                            sx={{ m: "1px" }}
                          />
                        ))}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell width="120px">Levels</TableCell>
                      <TableCell align="left">
                        {candidate.levels.map((level) => (
                          <Chip
                            key={level.id}
                            color={
                              filter.levels.includes(level.id)
                                ? "info"
                                : "default"
                            }
                            size="small"
                            label={level.name}
                            sx={{ m: "1px" }}
                          />
                        ))}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell width="120px">Specialties</TableCell>
                      <TableCell align="left">
                        {candidate.specialties.map((specialty) => (
                          <Chip
                            key={specialty.id}
                            color={
                              filter.specialties.includes(specialty.id)
                                ? "info"
                                : "default"
                            }
                            size="small"
                            label={specialty.name}
                            sx={{ m: "1px" }}
                          />
                        ))}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell width="120px">Areas</TableCell>
                      <TableCell align="left">
                        {candidate.areas.map((area) => (
                          <Chip
                            key={area.id}
                            color={
                              filter.areas.includes(area.id)
                                ? "info"
                                : "default"
                            }
                            size="small"
                            label={area.name}
                            sx={{ m: "1px" }}
                          />
                        ))}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell width="120px">Languages</TableCell>
                      <TableCell align="left">
                        {candidate.languages.map((language) => (
                          <Chip
                            key={language.id}
                            color={
                              filter.languages.includes(language.id)
                                ? "info"
                                : "default"
                            }
                            size="small"
                            label={language.name}
                            sx={{ m: "1px" }}
                          />
                        ))}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell width="120px">Qualifications</TableCell>
                      <TableCell align="left">
                        {candidate.qualifications.map((qualification) => (
                          <Chip
                            key={qualification.id}
                            size="small"
                            label={qualification.name}
                            sx={{ m: "1px" }}
                          />
                        ))}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default CandidateListTableRow;
